import clsx from 'clsx'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useContext, type ReactNode } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Checkbox } from '@wartek-id/checkbox-v1'

import {
  Table as KinerjaTable,
  TableBody,
  TableHeader,
  TableHeaderItem,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaShared/components/Table'
import TableRowStates from 'app/KinerjaShared/components/TableRowStates'
import {
  PERIODIK_PERFORMANCE_PRACTICE_RESULTS,
  PERIODIK_WORK_BEHAVIOR_RESULTS,
} from 'app/KinerjaShared/constants/periodik'
import {
  PENETAPAN_PEGAWAI_STATE_LABELS,
  PENETAPAN_PEGAWAI_PREDIKAT_RATING_OPTIONS,
} from 'app/KinerjaTimPenilaiPSPage/constants/penetapan'
import { TableContext } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/TablePenetapan/TableContext'
import { BadgePredikatKinerja } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/BadgePredikatKinerja'
import { BadgeStatusPengirimanPPK } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/BadgeStatusPengiriman'
import useBasePath from 'app/KinerjaTimPenilaiPSPage/hooks/useBasePath'
import { STEP_IDS } from 'app/KinerjaTimPenilaiPSPage/components/Penetapan/Onboarding/steps'
import { ActionButtonTK, ActionButtonPPK } from './ActionButton'

type TableProps = {
  nextPageResolver: (pengawasSekolahId: string) => string
  toolbar?: ReactNode
  tableId?: string
}
export function Table({ nextPageResolver, toolbar, tableId }: TableProps) {
  const router = useRouter()
  const context = useContext(TableContext)
  const {
    basePath,
    isTimKinerjaPengawasSekolahUrl: isTimKinerjaPengawasSekolah,
    isPPKPengawasSekolahUrl: isPPKPengawasSekolah,
  } = useBasePath()

  // Ensure that the component is used within the correct context provider
  if (!context)
    throw new Error(
      '<TablePegawaiPenetapan.Table> must be used within a <TablePegawaiPenetapan.Provider>'
    )

  // ------------------------------------
  // Get context data and its derivatives
  // ------------------------------------
  const {
    pengawasSekolahList,
    isFetching,
    isAllSentToPPK,
    selectedIds,
    selectableIds,
    onToggle,
    onToggleAll,
  } = context
  const isEmpty = pengawasSekolahList.length === 0
  const numOfSelectableIds = selectableIds.length

  // ----------------------
  // Header checkbox status
  // ----------------------
  const isAllChecked =
    numOfSelectableIds > 0 && numOfSelectableIds === selectedIds.size
  const isIndeterminate = selectedIds.size > 0 && !isAllChecked

  return (
    <div className="bg-white rounded-lg">
      {toolbar && (
        <div className="p-6">
          <div className="flex justify-between">{toolbar}</div>
        </div>
      )}
      <KinerjaTable rounded className="bg-white" id={tableId}>
        <TableHeader
          className={clsx(
            '!rounded-t-lg',
            isTimKinerjaPengawasSekolah
              ? '!bg-blue-80 !text-white'
              : '!bg-surface-subdued'
          )}
        >
          {isTimKinerjaPengawasSekolah && !isAllSentToPPK && (
            <TableHeaderItem>
              <div className="flex">
                <Checkbox
                  indeterminate={isIndeterminate}
                  checked={isAllChecked}
                  disabled={numOfSelectableIds === 0 || isFetching}
                  onChange={(event) => onToggleAll?.(event.target.checked)}
                />
              </div>
            </TableHeaderItem>
          )}
          <TableHeaderItem className="text-sm">Nama</TableHeaderItem>
          {isTimKinerjaPengawasSekolah && (
            <TableHeaderItem className="w-32 text-sm text-center">
              NIP
            </TableHeaderItem>
          )}
          <TableHeaderItem
            className={clsx(
              'text-sm',
              isTimKinerjaPengawasSekolah && 'text-center'
            )}
          >
            Instansi
          </TableHeaderItem>
          {isTimKinerjaPengawasSekolah && (
            <>
              <TableHeaderItem className="text-sm text-center">
                Rating Praktik Kinerja
              </TableHeaderItem>
              <TableHeaderItem className="text-sm text-center">
                Rating Perilaku Kerja
              </TableHeaderItem>
              <TableHeaderItem className="w-56 text-sm text-center">
                Predikat Kinerja
              </TableHeaderItem>
            </>
          )}
          {isPPKPengawasSekolah && (
            <>
              <TableHeaderItem className="w-56 text-sm">
                Rekomendasi Predikat Kinerja dari Tim Kinerja
              </TableHeaderItem>
              <TableHeaderItem className="w-56 text-sm">
                Perubahan Predikat Kinerja
              </TableHeaderItem>
            </>
          )}
          <TableHeaderItem
            className={clsx(
              'text-sm',
              isTimKinerjaPengawasSekolah && 'text-center'
            )}
          >
            {isTimKinerjaPengawasSekolah ? 'Status Pengiriman' : 'Status'}
          </TableHeaderItem>
          <TableHeaderItem className="text-sm text-center">
            {isTimKinerjaPengawasSekolah && 'Aksi'}
          </TableHeaderItem>
        </TableHeader>
        <TableBody>
          {isFetching ? (
            <TableShimmer columns={9} rows={10} />
          ) : isEmpty ? (
            <TableRowStates colSpan={9} type="EMPTY">
              <Text color="subdued">
                Belum ada data pegawai pada Penetapan Predikat Kinerja ini.
              </Text>
            </TableRowStates>
          ) : (
            <>
              {pengawasSekolahList.map((item, i) => {
                // Status and rating handling
                const predicateTK = item?.predikatKinerjaTk?.predikatKinerja
                const predicateFinal =
                  item?.predikatKinerjaFinal?.predikatKinerja

                const isDisabled = !['NOT_SENT'].includes(item.status)
                const workBehaviorResult = predicateTK
                  ? PERIODIK_WORK_BEHAVIOR_RESULTS?.[
                      item.predikatKinerjaTk?.perilakuKerjaRating
                    ]
                  : '-'
                const performancePracticeResult = predicateTK
                  ? PERIODIK_PERFORMANCE_PRACTICE_RESULTS?.[
                      item.predikatKinerjaTk?.praktikKinerjaRating
                    ]
                  : '-'

                const isInSelectedList = selectedIds.has(item.pengawasSekolahId)
                const isChecked = !isDisabled && isInSelectedList

                // Status label
                const statusLabel =
                  PENETAPAN_PEGAWAI_STATE_LABELS?.[item.status] ?? '-'

                return (
                  <TableRow
                    key={item.pengawasSekolahId + i}
                    className="font-semibold align-middle"
                  >
                    {isTimKinerjaPengawasSekolah && !isAllSentToPPK && (
                      <TableRowItem className={STEP_IDS.CHECKBOX_CLASSES}>
                        <div className="flex items-center">
                          <Checkbox
                            disabled={isDisabled || isFetching}
                            checked={isChecked}
                            onChange={(event) => {
                              onToggle?.(
                                item.pengawasSekolahId,
                                event.target.checked
                              )
                            }}
                          />
                        </div>
                      </TableRowItem>
                    )}
                    <TableRowItem>
                      <div className="overflow-wrap-anywhere">
                        <Text variant="body-sm">{item.namaPengawas}</Text>
                      </div>
                    </TableRowItem>
                    {isTimKinerjaPengawasSekolah && (
                      <TableRowItem>
                        <div className="overflow-wrap-anywhere !font-normal text-center">
                          <Text variant="body-sm">{item.nip}</Text>
                        </div>
                      </TableRowItem>
                    )}
                    <TableRowItem
                      className={clsx(
                        'overflow-wrap-anywhere !font-normal',
                        isTimKinerjaPengawasSekolah && 'text-center'
                      )}
                    >
                      <Text variant="body-sm">
                        {item.dinasPendidikan ?? '-'}
                      </Text>
                    </TableRowItem>
                    {isTimKinerjaPengawasSekolah && (
                      <>
                        <TableRowItem
                          className={
                            isTimKinerjaPengawasSekolah && 'text-center'
                          }
                        >
                          <Text
                            as="span"
                            variant="body-sm"
                            color={
                              !isDisabled && performancePracticeResult
                                ? 'default'
                                : 'subdued'
                            }
                            className="lg:text-nowrap"
                          >
                            {performancePracticeResult || '-'}
                          </Text>
                        </TableRowItem>
                        <TableRowItem
                          className={
                            isTimKinerjaPengawasSekolah && 'text-center'
                          }
                        >
                          <Text
                            as="span"
                            variant="body-sm"
                            color={
                              !isDisabled && workBehaviorResult
                                ? 'default'
                                : 'subdued'
                            }
                            className="lg:text-nowrap"
                          >
                            {workBehaviorResult || '-'}
                          </Text>
                        </TableRowItem>
                        <TableRowItem
                          className={
                            isTimKinerjaPengawasSekolah && 'text-center'
                          }
                        >
                          <BadgePredikatKinerja
                            predicate={predicateTK}
                            status={item?.status}
                          />
                        </TableRowItem>
                        <TableRowItem
                          className={
                            isTimKinerjaPengawasSekolah && 'text-center'
                          }
                        >
                          <div className="flex items-center gap-2 !font-normal justify-center">
                            <Text variant="body-sm">{statusLabel}</Text>
                          </div>
                        </TableRowItem>
                      </>
                    )}

                    {isPPKPengawasSekolah && (
                      <>
                        <TableRowItem>
                          <Text
                            as="span"
                            variant="body-sm"
                            color={
                              !isDisabled &&
                              predicateTK &&
                              !['NOT_RATED', 'NOT_SENT'].includes(item?.status)
                                ? 'default'
                                : 'subdued'
                            }
                            className="!font-semibold uppercase lg:text-nowrap"
                          >
                            {['NOT_RATED', 'NOT_SENT'].includes(item?.status)
                              ? '-'
                              : PENETAPAN_PEGAWAI_PREDIKAT_RATING_OPTIONS?.[
                                  predicateTK
                                ] ?? '-'}
                          </Text>
                        </TableRowItem>
                        <TableRowItem>
                          <Text
                            as="span"
                            variant="body-sm"
                            color={
                              !isDisabled && predicateFinal
                                ? 'default'
                                : 'subdued'
                            }
                            className="!font-semibold uppercase lg:text-nowrap"
                          >
                            {PENETAPAN_PEGAWAI_PREDIKAT_RATING_OPTIONS?.[
                              predicateFinal
                            ] ?? '-'}
                          </Text>
                        </TableRowItem>
                        <TableRowItem>
                          <div className="flex items-center gap-2 !font-normal justify-start">
                            <BadgeStatusPengirimanPPK status={item?.status} />
                          </div>
                        </TableRowItem>
                      </>
                    )}

                    <TableRowItem
                      className={clsx('text-center', STEP_IDS.ACTION_CLASSES)}
                    >
                      {isTimKinerjaPengawasSekolah && (
                        <Link
                          href={nextPageResolver(item.pengawasSekolahId)}
                          passHref
                        >
                          <ActionButtonTK
                            data={item}
                            className="mx-auto"
                            onClick={() => {
                              router.push(
                                `${basePath}/penilai/penetapan/${item.pengawasSekolahId}`
                              )
                            }}
                          />
                        </Link>
                      )}

                      {isPPKPengawasSekolah && <ActionButtonPPK data={item} />}
                    </TableRowItem>
                  </TableRow>
                )
              })}
            </>
          )}
        </TableBody>
      </KinerjaTable>
    </div>
  )
}
